import React, { useEffect, useState } from 'react';
import { adminApi } from '../../api/client';
import Spinner from '../../components/common/Spinner';
import { FaSchool, FaUpload, FaPalette, FaCheck } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

export default function Settings() {
  const { school } = useAuth();
  const [settings, setSettings] = useState(null);
  const [form,     setForm]     = useState({});
  const [saving,   setSaving]   = useState(false);
  const [logo,     setLogo]     = useState(null);
  const [preview,  setPreview]  = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    adminApi.getSettings()
      .then(r => {
        setSettings(r.data.data);
        setForm(r.data.data);
        setPreview(r.data.data.logo);
      })
      .catch(() => {});
  };

  useEffect(() => { load(); }, []);

  // Apply color preview live to the sidebar CSS variable
  useEffect(() => {
    if (form.primary_color) {
      document.documentElement.style.setProperty('--school-primary', form.primary_color);
    }
  }, [form.primary_color]);

  const updateSchoolContext = (newData) => {
    // Update AuthContext so sidebar reflects changes immediately without logout
    const updatedSchool = {
      ...(school || {}),
      ...newData,
    };
    localStorage.setItem('school', JSON.stringify(updatedSchool));
    // Trigger re-render by updating localStorage value — AuthContext reads this
    window.dispatchEvent(new Event('storage'));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi.updateSettings({
        name: form.name, address: form.address, phone: form.phone,
        email: form.email, website: form.website, motto: form.motto,
        principal_name: form.principal_name,
        primary_color: form.primary_color, secondary_color: form.secondary_color,
      });
      updateSchoolContext({
        name: form.name,
        primary_color: form.primary_color,
        secondary_color: form.secondary_color,
      });
      toast.success('Settings saved successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleLogoUpload = async () => {
    if (!logo) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('logo', logo);
    // Also send token as form field — fallback for when Authorization header is stripped
    const token = localStorage.getItem('access_token');
    if (token) fd.append('_token', token);
    try {
      const res = await adminApi.uploadLogo(fd);
      const logoUrl = res.data.data.logo_url;
      setPreview(logoUrl);
      setLogo(null);
      updateSchoolContext({ logo: logoUrl });
      toast.success('Logo uploaded successfully');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Upload failed';
      toast.error(msg);
      console.error('Logo upload error:', err.response?.data);
    } finally { setUploading(false); }
  };

  if (!settings) return <Spinner />;

  const primary = form.primary_color || '#0d6efd';

  const field = (label, key, type = 'text') => (
    <div style={{ marginBottom: 16 }}>
      <label className="sms-label">{label}</label>
      {type === 'color' ? (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input type="color" value={form[key] || '#0d6efd'} onChange={e => setForm({ ...form, [key]: e.target.value })}
            style={{ width: 48, height: 38, border: 'none', borderRadius: 8, cursor: 'pointer', padding: 2 }} />
          <input className="sms-input" value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })} style={{ flex: 1 }} />
        </div>
      ) : (
        <input className="sms-input" type={type} value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })} />
      )}
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <h2>School Settings</h2>
        <p>Manage your school profile, logo, and branding</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>

        {/* Logo upload */}
        <div className="sms-card">
          <h5 style={{ fontWeight: 600, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FaSchool color={primary} /> School Logo
          </h5>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            {/* Logo preview */}
            <div style={{
              width: 140, height: 140, borderRadius: 20,
              background: preview ? 'transparent' : '#f0f2f5',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', border: `3px dashed ${preview ? primary : '#e5e7eb'}`,
              position: 'relative',
            }}>
              {preview
                ? <img src={preview} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <div style={{ textAlign: 'center' }}>
                    <FaSchool size={48} color="#d1d5db" />
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 8 }}>No logo yet</div>
                  </div>
              }
            </div>

            {/* File picker */}
            <label style={{ cursor: 'pointer', width: '100%' }}>
              <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }}
                onChange={e => {
                  if (e.target.files[0]) {
                    setLogo(e.target.files[0]);
                    setPreview(URL.createObjectURL(e.target.files[0]));
                  }
                }} />
              <div className="sms-btn sms-btn-outline" style={{ width: '100%', justifyContent: 'center' }}>
                <FaUpload size={13} /> Choose Image
              </div>
            </label>

            {logo && (
              <button
                onClick={handleLogoUpload}
                disabled={uploading}
                className="sms-btn sms-btn-primary"
                style={{ width: '100%', justifyContent: 'center', background: primary }}>
                {uploading ? 'Uploading...' : <><FaCheck size={13} /> Upload Logo</>}
              </button>
            )}

            <p style={{ color: '#9ca3af', fontSize: '0.75rem', textAlign: 'center', margin: 0 }}>
              PNG, JPG or WebP · Recommended 200×200px · Max 10MB
            </p>
          </div>
        </div>

        {/* Branding colors */}
        <div className="sms-card">
          <h5 style={{ fontWeight: 600, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FaPalette color={primary} /> Theme Colors
          </h5>
          {field('Primary Color', 'primary_color', 'color')}
          {field('Secondary Color', 'secondary_color', 'color')}

          {/* Live preview */}
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginBottom: 8 }}>Live Preview</div>
            <div style={{ borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              {/* Sidebar preview */}
              <div style={{ background: '#1a1a2e', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {preview
                    ? <img src={preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                    : <FaSchool color="#fff" size={15} />}
                </div>
                <div>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.85rem', lineHeight: 1 }}>{form.name || 'School Name'}</div>
                  <div style={{ color: '#8899aa', fontSize: '0.68rem' }}>ADMIN</div>
                </div>
              </div>
              {/* Nav item preview */}
              <div style={{ background: '#1a1a2e', padding: '0 0 8px' }}>
                <div style={{ padding: '9px 16px', borderLeft: `3px solid ${primary}`, background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: primary }} /> Dashboard
                </div>
                <div style={{ padding: '9px 16px', color: '#b0b8cc', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#4b5563' }} /> Settings
                </div>
              </div>
              {/* Button preview */}
              <div style={{ background: '#f9fafb', padding: '12px 16px' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: primary, color: '#fff', padding: '6px 14px', borderRadius: 8, fontSize: '0.8rem', fontWeight: 600 }}>
                  <FaCheck size={10} /> Save Changes
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="sms-btn sms-btn-primary"
            style={{ marginTop: 16, width: '100%', justifyContent: 'center', background: primary }}>
            {saving ? 'Saving...' : <><FaCheck size={13} /> Apply Theme</>}
          </button>
        </div>
      </div>

      {/* School information */}
      <div className="sms-card">
        <h5 style={{ fontWeight: 600, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
          <FaSchool color={primary} /> School Information
        </h5>
        <form onSubmit={handleSave}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 16 }}>
            {field('School Name', 'name')}
            {field("Principal's Name", 'principal_name')}
            {field('School Motto', 'motto')}
            {field('Phone', 'phone', 'tel')}
            {field('Email', 'email', 'email')}
            {field('Website', 'website', 'url')}
            {field('Address', 'address')}
          </div>
          <button type="submit" disabled={saving} className="sms-btn sms-btn-primary"
            style={{ marginTop: 8, background: primary }}>
            {saving ? 'Saving...' : 'Save Information'}
          </button>
        </form>
      </div>
    </div>
  );
}
